import React, { useState, useRef, useEffect } from 'react';
import Icon from 'components/AppIcon';

const MFAVerifyStep = ({ onVerify, onBack, loading, error }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs?.current?.[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/?.test(value)) return;
    const newCode = [...code];
    newCode[index] = value?.slice(-1);
    setCode(newCode);
    if (value && index < 5) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !code?.[index] && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pasted = e?.clipboardData?.getData('text')?.replace(/\D/g, '')?.slice(0, 6);
    if (pasted?.length === 6) {
      setCode(pasted?.split(''));
      inputRefs?.current?.[5]?.focus();
    }
  };

  const fullCode = code?.join('');

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (fullCode?.length === 6) onVerify(fullCode);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: 'rgba(52, 102, 244, 0.12)' }}
        >
          <Icon name="ShieldCheck" size={28} color="var(--color-accent)" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="text-base font-semibold" style={{ color: 'var(--color-foreground)' }}>
            Two-Factor Authentication
          </h3>
          <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
            Enter the 6-digit code from your authenticator app
          </p>
        </div>
      </div>
      {/* Error */}
      {error && (
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-lg"
          style={{
            backgroundColor: 'rgba(220, 38, 38, 0.08)',
            border: '1px solid rgba(220, 38, 38, 0.25)',
          }}
        >
          <Icon name="AlertCircle" size={16} color="var(--color-error)" strokeWidth={2} className="mt-0.5 flex-shrink-0" />
          <span className="text-sm" style={{ color: 'var(--color-error)' }}>{error}</span>
        </div>
      )}
      {/* Code inputs */}
      <div className="flex gap-2 justify-center" onPaste={handlePaste}>
        {code?.map((digit, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(i, e?.target?.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={loading}
            className="w-11 h-13 text-center text-xl font-bold rounded-lg transition-all duration-200"
            style={{
              width: '44px',
              height: '52px',
              fontFamily: 'var(--font-data)',
              fontVariantNumeric: 'tabular-nums',
              backgroundColor: 'var(--color-input)',
              border: digit ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
              color: 'var(--color-foreground)',
              outline: 'none',
              boxShadow: digit ? '0 0 0 3px rgba(6, 214, 245, 0.12)' : 'none',
            }}
          />
        ))}
      </div>
      {/* Verify button */}
      <button
        type="submit"
        disabled={loading || fullCode?.length < 6}
        className="w-full h-11 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all duration-200"
        style={{
          backgroundColor: loading || fullCode?.length < 6 ? 'var(--color-muted)' : 'var(--color-primary)',
          color: loading || fullCode?.length < 6 ? 'var(--color-muted-foreground)' : '#FFFFFF',
          cursor: loading || fullCode?.length < 6 ? 'not-allowed' : 'pointer',
          boxShadow: loading || fullCode?.length < 6 ? 'none' : 'var(--shadow-md)',
        }}
      >
        {loading ? (
          <>
            <div
              className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }}
            />
            Verifying…
          </>
        ) : (
          <>
            <Icon name="ShieldCheck" size={16} strokeWidth={2} />
            Verify Code
          </>
        )}
      </button>
      {/* Back */}
      <button
        type="button"
        onClick={onBack}
        disabled={loading}
        className="flex items-center justify-center gap-1.5 text-sm transition-colors"
        style={{ color: 'var(--color-muted-foreground)' }}
      >
        <Icon name="ArrowLeft" size={14} strokeWidth={2} />
        Back to login
      </button>
    </form>
  );
};

export default MFAVerifyStep;
